import React, { useEffect } from "react";
import {
  DocumentTextIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";

const TermsOfService: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-orange via-orange-500 to-accent-red text-white pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <DocumentTextIcon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Terms of Service
            </h1>
            <p className="text-xl opacity-95 mb-2">
              Our commitment to transparent business relationships
            </p>
            <p className="text-lg opacity-80">Last updated: January 2024</p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {/* Acceptance of Terms */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <ScaleIcon className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  1. Acceptance of Terms
                </h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                By accessing and using JSONPLAY's services, you accept and agree
                to be bound by the terms and provision of this agreement. If you
                do not agree to abide by the above, please do not use this
                service.
              </p>
            </div>

            {/* Services Description */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  2. Description of Service
                </h2>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                JSONPLAY provides software development services including but
                not limited to:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Website Development",
                  "Mobile App Development",
                  "UI/UX Design Services",
                  "Full-stack Development",
                  "Custom Development Solutions",
                ].map((service, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg border-l-4 border-blue-500"
                  >
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      <span className="text-gray-700 font-medium">
                        {service}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Terms */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                  <CreditCardIcon className="w-6 h-6 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  3. Payment Terms
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    title: "Payment Schedule",
                    desc: "Payments are due according to the schedule outlined in your project agreement.",
                  },
                  {
                    title: "Late Payments",
                    desc: "Late payments may result in project suspension and additional fees.",
                  },
                  {
                    title: "Refunds",
                    desc: "Refund policies are outlined in individual project agreements.",
                  },
                  {
                    title: "Disputes",
                    desc: "Payment disputes must be raised within 30 days of invoice date.",
                  },
                ].map((item, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {item.title}
                    </h4>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                  <ShieldCheckIcon className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  4. Intellectual Property
                </h2>
              </div>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg border-l-4 border-green-500">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Client-Owned Content
                  </h4>
                  <p className="text-gray-600">
                    You retain ownership of all content, data, and materials you
                    provide to us. You grant us a license to use these materials
                    solely for the purpose of delivering the agreed services.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg border-l-4 border-blue-500">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Developed Solutions
                  </h4>
                  <p className="text-gray-600 mb-4">
                    Upon full payment, you will own the final deliverables
                    specifically created for your project. JSONPLAY retains
                    rights to:
                  </p>
                  <ul className="space-y-2">
                    {[
                      "General methodologies and know-how",
                      "Pre-existing tools and frameworks",
                      "Code libraries developed independently",
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-600 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Client Responsibilities */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                  <UserGroupIcon className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  5. Client Responsibilities
                </h2>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                To ensure successful project completion, clients agree to:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Provide timely feedback and approvals",
                  "Supply necessary content, assets, and information",
                  "Make key decision-makers available for consultations",
                  "Maintain open and clear communication",
                  "Provide access to necessary systems and accounts",
                ].map((responsibility, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg flex items-start"
                  >
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{responsibility}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Terms Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  6. Project Changes
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Changes to project scope, timeline, or deliverables may result
                  in additional costs and time requirements. All changes must be
                  agreed upon in writing before implementation.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  7. Confidentiality
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  JSONPLAY agrees to maintain the confidentiality of all
                  proprietary information shared during the course of our
                  business relationship. We will not disclose client information
                  to third parties without explicit consent.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  8. Service Warranty
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We warrant that our services will be performed in a
                  professional manner in accordance with industry standards. We
                  will correct any defects in our work at no additional cost for
                  a period specified in your project agreement.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  9. Limitation of Liability
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  JSONPLAY's liability for any claim relating to our services
                  shall not exceed the total amount paid by the client for the
                  specific project giving rise to the claim.
                </p>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                    Important Notice
                  </h3>
                  <p className="text-yellow-700 text-sm leading-relaxed">
                    These terms may be updated from time to time. Material
                    changes will be communicated to clients with reasonable
                    notice. Continued use of our services constitutes acceptance
                    of modified terms.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-gradient-to-br from-primary-orange to-accent-red text-white p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                  <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold">
                  Questions About These Terms?
                </h2>
              </div>
              <p className="mb-6 opacity-95 leading-relaxed">
                If you have questions about these Terms of Service, please don't
                hesitate to contact us:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Email</h4>
                  <a
                    href="mailto:hello@jsonplay.com"
                    className="hover:underline"
                  >
                    hello@jsonplay.com
                  </a>
                </div>
                <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Phone</h4>
                  <a href="tel:+917017353583" className="hover:underline">
                    +91 70173 53583
                  </a>
                </div>
                <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">WhatsApp</h4>
                  <a
                    href="https://wa.me/917017353583?text=Hi! I have questions about your Terms of Service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
                        return (window as any).gtag_report_conversion('https://wa.me/917017353583?text=Hi! I have questions about your Terms of Service');
                      }
                      return true;
                    }}
                  >
                    Chat with us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
